import { memo, useEffect, useState } from "react";
import { PanelHeader } from "./PanelHeader";

// Static GeoJSON lives on the same origin as the web bundle (Vite public/geo
// at kmitl.nonarkara.org/geo), not the API host. The API has no /geo/* route.
// Resolve relative to the current page so dev (Vite at :5173) and prod
// (kmitl.nonarkara.org) both work without a Vite env-var dependency.
const GEO_BASE =
  (import.meta.env.VITE_GEO_BASE_URL as string | undefined) ||
  (typeof window !== "undefined" ? window.location.origin : "");

/** KMITL-bbox waterway network — sourced from
 *  apps/web/public/geo/kmitl-waterways.geojson. We fetch the file at
 *  runtime so the panel stays in sync with edits to the asset. */
interface WaterwayProps {
  id: string;
  /** OSM way/relation id — the feature's real identity, for citation. */
  osmId?: number;
  name: string;
  nameTh?: string;
  kind: "canal" | "drain";
  /** Verbatim OSM waterway value: river | canal | stream | drain | ditch. */
  osmClass?: string;
  basin?: string;
  basinLocal?: string;
  /** Design cross-section, by class (MWA tables) — not surveyed. */
  widthM?: number;
  depthM?: number;
  /** Traced length off the OSM geometry, metres. */
  lengthM?: number;
  upstream?: [number, number];
  downstream?: [number, number];
  tier?: string;
}

interface WaterwayFeature {
  type: "Feature";
  properties: WaterwayProps;
  geometry: { type: "LineString"; coordinates: [number, number][] };
}

interface WaterwaysFC {
  type: "FeatureCollection";
  features: WaterwayFeature[];
}

/** Group waterways by basin → each basin is a section in the panel. */
function groupByBasin(fcs: WaterwaysFC): Map<string, WaterwayFeature[]> {
  const map = new Map<string, WaterwayFeature[]>();
  for (const f of fcs.features) {
    const b = f.properties.basinLocal ?? f.properties.basin ?? "Other";
    const arr = map.get(b) ?? [];
    arr.push(f);
    map.set(b, arr);
  }
  // Sort each basin longest-first: the trunk channel is what the operator
  // needs to see first, and OSM gives no flow-direction tag to sort by.
  for (const arr of map.values()) {
    arr.sort(
      (a, b) =>
        (b.properties.lengthM ?? 0) - (a.properties.lengthM ?? 0) ||
        a.properties.name.localeCompare(b.properties.name),
    );
  }
  return map;
}

/** Tiny inline SVG that lays each waterway out as a horizontal line with
 *  a flow arrow at the right end. Width is ∝ widthM, color matches the
 *  deck.gl layer (cyan canal, teal drain). */
function WatershedSchematic({ features }: { features: WaterwayFeature[] }) {
  const maxW = Math.max(...features.map((f) => f.properties.widthM ?? 6), 1);
  return (
    <svg
      className="watershed-schematic"
      viewBox="0 0 300 200"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Waterway flow schematic"
    >
      {features.map((f, i) => {
        const w = f.properties.widthM ?? 6;
        const h = 4 + (w / maxW) * 22;
        const y = 12 + i * 22;
        const isCanal = f.properties.kind === "canal";
        const color = isCanal ? "#38BDF8" : "#6EE7B7";
        return (
          <g key={f.properties.id}>
            <line
              x1="6"
              y1={y + h / 2}
              x2="270"
              y2={y + h / 2}
              stroke={color}
              strokeWidth={h}
              strokeLinecap="round"
              opacity={isCanal ? 0.85 : 0.65}
            />
            {/* Flow direction arrow at the right */}
            <polygon
              points={`266,${y + h / 2 - 5} 286,${y + h / 2} 266,${y + h / 2 + 5}`}
              fill={color}
            />
            <text
              x="6"
              y={y - 1}
              fontFamily="IBM Plex Mono, monospace"
              fontSize="7"
              fill="#9CA3AF"
            >
              {f.properties.name} {f.properties.widthM ? `· ${f.properties.widthM}m` : ""}
            </text>
          </g>
        );
      })}
      <text
        x="290"
        y="195"
        fontFamily="IBM Plex Mono, monospace"
        fontSize="6"
        fill="#6B7280"
        textAnchor="end"
      >
        → flow direction
      </text>
    </svg>
  );
}

interface Props {
  /** Optional callback to fly the map to a coordinate. */
  onFlyTo?: (lng: number, lat: number, zoom?: number) => void;
}

/**
 * Watershed panel — renders the KMITL waterway network as a per-basin list
 * with an inline flow-direction schematic. Pairs with the deck.gl
 * `waterways` + `waterways-flow` map layers; clicking a row in the panel
 * flies the map to that channel.
 */
export const WaterWatershedPanel = memo(function WaterWatershedPanel({
  onFlyTo,
}: Props) {
  const [fcs, setFcs] = useState<WaterwaysFC | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    const url = `${GEO_BASE}/geo/kmitl-waterways.geojson`;
    fetch(url, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((j: WaterwaysFC) => setFcs(j))
      .catch((e: unknown) =>
        setLoadError(e instanceof Error ? e.message : "load failed"),
      );
  }, []);

  if (loadError) {
    return (
      <section className="left-section water-watershed" aria-label="Watershed waterway network">
        <PanelHeader
          title="WATERSHED"
          source="openstreetmap"
          fallbackTier="unavailable"
        />
        <p className="caption mono" style={{ color: "var(--ink-low)" }}>
          Waterway file unavailable — {loadError}
        </p>
      </section>
    );
  }
  if (!fcs) {
    return (
      <section className="left-section water-watershed" aria-label="Watershed waterway network">
        <PanelHeader
          title="WATERSHED"
          source="openstreetmap"
        />
        <p className="caption mono" style={{ color: "var(--ink-low)" }}>
          Loading khlong network…
        </p>
      </section>
    );
  }

  const basins = groupByBasin(fcs);
  const namedCount = fcs.features.filter((f) => f.properties.nameTh).length;
  const canalCount = fcs.features.filter((f) => f.properties.kind === "canal").length;
  const drainCount = fcs.features.filter((f) => f.properties.kind === "drain").length;

  return (
    <section className="left-section water-watershed" aria-label="Watershed waterway network">
      <PanelHeader
        title="WATERSHED"
        source="openstreetmap"
        fallbackTier="live"
      />

      <div className="watershed-summary">
        <div className="watershed-count">
          <span className="mono watershed-count-num">{canalCount}</span>
          <span className="caption">canals</span>
        </div>
        <div className="watershed-count">
          <span className="mono watershed-count-num">{drainCount}</span>
          <span className="caption">drains</span>
        </div>
        <div className="watershed-count">
          <span className="mono watershed-count-num">
            {(fcs.features.reduce((t, f) => t + (f.properties.lengthM ?? 0), 0) / 1000).toFixed(0)}
          </span>
          <span className="caption">km traced</span>
        </div>
      </div>

      <WatershedSchematic features={fcs.features} />

      <p className="caption mono watershed-foot">
        Channel geometry is verbatim OpenStreetMap ({namedCount} named · © OpenStreetMap contributors, ODbL).
        Widths are design values by OSM class, not surveyed. Flow arrows are derived
        downstream-to-river from the network topology — OSM carries no flow tag here.
      </p>

      <p className="mono eyebrow watershed-basin-head">BY BASIN · sub-catchment</p>
      {Array.from(basins.entries()).map(([basin, items]) => (
        <details
          key={basin}
          className="watershed-basin"
          open={basins.size <= 2}
        >
          <summary className="mono watershed-basin-name">
            <span className="watershed-basin-arrow">▸</span>
            {basin}
            <span className="watershed-basin-count">{items.length}</span>
          </summary>
          <ul className="watershed-list">
            {items.map((f) => {
              const coords = f.geometry.coordinates;
              const mid = coords[Math.floor(coords.length / 2)];
              return (
                <li key={f.properties.id}>
                  <button
                    type="button"
                    className="watershed-row"
                    onClick={() => onFlyTo?.(mid[0], mid[1], 14)}
                    title={`${f.properties.name}${f.properties.nameTh ? ` (${f.properties.nameTh})` : ""} · OSM ${f.properties.osmClass ?? "waterway"} ${f.properties.osmId ?? ""} · fly to`}
                  >
                    <span
                      className="watershed-kind"
                      style={{ color: f.properties.kind === "canal" ? "var(--good, #38BDF8)" : "#6EE7B7" }}
                    >
                      {f.properties.kind === "canal" ? "C" : "D"}
                    </span>
                    <span className="watershed-row-name">
                      <span className="watershed-row-en">{f.properties.name}</span>
                      {f.properties.nameTh && (
                        <span className="watershed-row-th caption">{f.properties.nameTh}</span>
                      )}
                    </span>
                    <span className="watershed-row-meta mono caption">
                      {f.properties.osmClass && (
                        <span className="watershed-row-target">{f.properties.osmClass}</span>
                      )}
                      {f.properties.lengthM != null && (
                        <span>
                          {f.properties.lengthM >= 1000
                            ? `${(f.properties.lengthM / 1000).toFixed(1)} km`
                            : `${f.properties.lengthM} m`}
                        </span>
                      )}
                      {f.properties.widthM != null && (
                        <span>{f.properties.widthM}m wide</span>
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </details>
      ))}

      <p className="caption mono watershed-foot">
        Coordinates ordered upstream→downstream · line width ∝ channel width · click to fly
      </p>
    </section>
  );
});
